import { useQueryClient } from '@tanstack/react-query';
import { observer } from 'mobx-react';
import React, { useState } from 'react';

import { AxiosError } from 'axios';
import { ErrorMessage } from 'formik';
import { Firm, VirtualOffice } from 'types/firm';
import {
  FeeScheduleOption,
  FirmOptions,
  FormAction,
  GeographicParameterType,
  VirtualOfficeFormValues,
} from 'types/firmInfo';

import { Spacer, makeStyles } from '@bequestinc/wui';
import { FormControl, FormHelperText, FormLabel, Grid, TextField, Typography } from '@mui/material';

import { useGlobalContext } from 'hooks';
import { useGetFeeSchedulesQuery } from 'hooks/query-hooks/fee-schedules/useFeeScheduleQuery';
import { FirmsQueryKey, useFirmOptionsQuery } from 'hooks/query-hooks/firms/useFirmsQuery';
import {
  useCreateVirtualOfficeMutation,
  useUpdateVirtualOfficeMutation,
} from 'hooks/query-hooks/offices/useOfficeMutation';
import { useCurrentUserQuery } from 'hooks/query-hooks/users/useUserQuery';

import {
  ACTIVE,
  ADD,
  CAN_EDIT_FEE_SCHEDULE,
  CAN_EDIT_OFFICE_ATTORNEYS_AND_SERVICES,
  CONFIRM,
  LANGUAGES,
  ON_HOLD,
  REGISTER,
  UPDATE,
  VIRTUAL_OFFICE_COMMUNICATION_OPTIONS,
} from 'utils/constants';
import { PREVIOUSLY_DELETED_OFFICE_MESSAGE } from 'utils/constants/firmInfo';
import parseErrors from 'utils/errors';
import formatServiceForSubmit from 'utils/office';
import formatPhoneNumber from 'utils/validators';

import ChipSelector from 'components/ChipSelector';
import PhoneNumberField from 'components/custom_fields/PhoneNumberField';
import Checkbox from 'components/form/Checkbox';
import CheckboxGroup from 'components/form/CheckboxGroup';
import Form from 'components/form/Form';
import FormAreaWithAlert from 'components/form/FormAreaWithAlert';
import SelectField from 'components/form/SelectField';
import AttorneyServicesSelector from 'components/pages/firm-info/AttorneyServicesSelector';
import OfficeFormButtons from 'components/pages/firm-info/OfficeFormButtons';
import ReactivateOfficeModal from 'components/pages/firm-info/ReactivateOfficeModal';
import VirtualOfficeGeographicParameters from 'components/pages/firm-info/VirtualOfficeGeographicParameters';
import { virtualOfficeSchema } from 'components/pages/firm-info/schema';

const useStyles = makeStyles()({
  textField: {
    width: '100%',
  },
  card: {
    padding: '30px',
  },
  underReviewText: {
    marginTop: '10px',
    marginBottom: '20px',
  },
});

interface PropTypes {
  virtualOffice: Partial<VirtualOffice>;
  canUpdate: boolean;
  action: FormAction;
  afterSubmitAction?: () => void;
}

const VirtualOfficeForm = ({
  virtualOffice,
  canUpdate,
  action,
  afterSubmitAction = () => {},
}: PropTypes) => {
  const { classes } = useStyles();
  const { services } = useGlobalContext();

  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState('');
  const [showReactivateOfficeModal, setShowReactivateOfficeModal] = useState(false);

  const { data: user } = useCurrentUserQuery();
  const { data: firmOptions = { culturalFluency: [], specialServices: [] } } =
    useFirmOptionsQuery() as { data?: FirmOptions };
  const { data: feeSchedules = [] } = useGetFeeSchedulesQuery({ inNetwork: true }) as {
    data?: FeeScheduleOption[];
  };

  const addToCache = (data: VirtualOffice) =>
    queryClient.setQueryData<Firm>([...FirmsQueryKey, virtualOffice.firm], oldData => ({
      ...oldData,
      virtualOffices: oldData.virtualOffices.concat(data),
    }));

  const updateCache = (data: VirtualOffice) =>
    queryClient.setQueryData<Firm>([...FirmsQueryKey, virtualOffice.firm], oldData => ({
      ...oldData,
      virtualOffices: oldData.virtualOffices.map(oldOffice =>
        oldOffice.id === data.id ? data : oldOffice,
      ),
    }));

  const canPanelEditAttorneysAndServices = user?.isPanelUser
    ? user.groups?.includes(CAN_EDIT_OFFICE_ATTORNEYS_AND_SERVICES)
    : true;

  const createVirtualOfficeMutation = useCreateVirtualOfficeMutation({
    onSuccess: (data: VirtualOffice | string) => {
      if (typeof data === 'string' && data.includes(PREVIOUSLY_DELETED_OFFICE_MESSAGE)) {
        setShowReactivateOfficeModal(true);
        return;
      }
      addToCache(data as VirtualOffice);
      afterSubmitAction();
    },
    onError: (error: AxiosError) => {
      setErrorMessage(parseErrors(error.response));
    },
  });

  const updateVirtualOfficeMutation = useUpdateVirtualOfficeMutation({
    onSuccess: (data: VirtualOffice) => {
      setErrorMessage('');
      updateCache(data);
      afterSubmitAction();
    },
    onError: (error: AxiosError) => {
      setErrorMessage(parseErrors(error.response));
    },
  });

  const handleFormSubmit = async (formValues: VirtualOfficeFormValues) => {
    const formattedValues = formatServiceForSubmit(services, formValues) as VirtualOfficeFormValues;
    formattedValues.attorneyInfos = virtualOffice.firmAttorneys
      .filter(atty => (formValues.attorneyInfos as string[]).includes(atty.name))
      .map(atty => atty.pk);

    if (formattedValues.geographicParameterType === 'radius') {
      formattedValues.state = '';
    } else if (formattedValues.geographicParameterType === 'entireState') {
      formattedValues.zipCode = '';
      // only set radius to null if the state does not an update that needs to be approved.
      if (action === ADD || user.isPanelRepUser) formattedValues.radius = null;
    }
    if (!formattedValues.geographicParameterType) {
      setErrorMessage('Please select a geographic parameter type');
      return;
    }

    delete formattedValues.geographicParameterType;

    if (action === ADD) {
      const response = await createVirtualOfficeMutation.mutateAsync(formattedValues);
      // Prevent the success popup when a previously deleted office needs reactivation.
      if (typeof response === 'string' && response.includes(PREVIOUSLY_DELETED_OFFICE_MESSAGE)) {
        throw new Error('Virtual office reactivation required');
      }
    } else {
      await updateVirtualOfficeMutation.mutateAsync(formattedValues);
    }
  };

  const initialGeoParameterType = (): GeographicParameterType | null => {
    if (virtualOffice?.zipCode) {
      return 'radius';
    }
    if (virtualOffice?.state) {
      return 'entireState';
    }

    return null;
  };

  const initialStateZipCodeRadiusValues = () => {
    if (virtualOffice?.zipCode) {
      return {
        state: '',
        zipCode: virtualOffice.zipCode,
        radius: virtualOffice.radius,
      };
    }

    return {
      state: virtualOffice?.state || '',
      zipCode: '',
      // leave radius to respect the value of a pending radius for the change to a radius-based office.
      // zip code remains null until it is approved.
      radius: virtualOffice?.radius,
    };
  };

  const virtualOfficeHasAddressFieldsUnderReview = Object.keys(
    virtualOffice?.fieldsUnderReview || {},
  ).some(key => ['state', 'zipCode'].includes(key));

  return (
    <Form
      initialValues={{
        status: virtualOffice.status || ACTIVE,
        id: virtualOffice.id || null,
        firm: virtualOffice.firm || null,
        geographicParameterType: initialGeoParameterType(),
        communicationPreference: virtualOffice.communicationPreference || [],
        email: virtualOffice.email || '',
        phone: formatPhoneNumber(virtualOffice.phone) || '',
        phoneExtension: virtualOffice.phoneExtension || '',
        languages: virtualOffice.languages || ([ADD, REGISTER].includes(action) ? ['English'] : []),
        engagementScore: [ADD, REGISTER].includes(action) ? 5 : virtualOffice.engagementScore,
        serviceTypes: virtualOffice.serviceTypes || [],
        specialServices:
          firmOptions?.specialServices?.filter(service =>
            virtualOffice.specialServices?.includes(service[0]),
          ) || [],
        fieldsUnderReview: virtualOffice.fieldsUnderReview,
        addressInfoConfirmed: false,
        serviceInfoConfirmed: false,
        forConfirmationModal: action === CONFIRM,
        feeSchedule: virtualOffice.feeSchedule,
        attorneyInfos: virtualOffice.attorneyInfos?.map(atty => atty.name) || [],
        ...initialStateZipCodeRadiusValues(),
      }}
      validationSchema={virtualOfficeSchema}
      handleFormSubmit={handleFormSubmit}
      validateOnChange
      validateOnBlur
      validateOnMount
      doNotRenderButtons
      enableReinitialize
      readOnly={!canUpdate}
      initialTouched={{
        addressInfoConfirmed: true,
        serviceInfoConfirmed: true,
        attorneyInfos: true,
        languages: true,
        email: true,
        phone: true,
        serviceTypes: true,
      }}
      showErrors={false}
      shouldReset={false}
    >
      <Grid container direction="column" spacing={2} className={classes.card}>
        <Grid item>
          <Typography variant="h5">Communication</Typography>
        </Grid>
        <Grid item>
          <TextField
            name="email"
            label="Email address"
            className={classes.textField}
            disabled={!canUpdate}
          />
        </Grid>

        <Grid item xs={6}>
          <PhoneNumberField
            name="phone"
            label="Phone"
            className={classes.textField}
            disabled={!canUpdate}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            name="phoneExtension"
            label="Phone Extension"
            inputProps={{
              maxLength: 6,
            }}
            className={classes.textField}
            disabled={!canUpdate}
          />
        </Grid>
        <Spacer v={16} />
        <Grid item>
          <FormControl component="fieldset">
            <FormLabel
              component="legend"
              sx={{
                color: 'black',
              }}
              focused={false}
            >
              What teleconferencing product do you use?
            </FormLabel>
            <CheckboxGroup
              name="communicationPreference"
              type="checkbox-group"
              options={VIRTUAL_OFFICE_COMMUNICATION_OPTIONS}
            />
          </FormControl>
        </Grid>

        <Grid item>
          <Typography variant="h5">Geographic Parameters</Typography>
        </Grid>

        <FormAreaWithAlert active={Boolean(action === CONFIRM)}>
          {virtualOfficeHasAddressFieldsUnderReview && (
            <Typography color="text.disabled" className={classes.underReviewText}>
              Cannot edit address while changes are under review by MLP
            </Typography>
          )}
          <Grid item>
            <FormControl component="fieldset">
              <FormLabel
                component="legend"
                sx={{
                  color: 'primary.main',
                }}
                focused={false}
              >
                Please choose either entire state or radius option.
              </FormLabel>
              <FormLabel
                component="legend"
                sx={{
                  color: 'black',
                }}
                focused={false}
              >
                I want to support the area around my virtual office
              </FormLabel>
            </FormControl>
          </Grid>
          <Grid item>
            <VirtualOfficeGeographicParameters
              virtualOffice={virtualOffice}
              canUpdate={canUpdate}
            />
          </Grid>

          <Spacer v={10} />
          {action === CONFIRM && (
            <Checkbox
              type="checkbox"
              name="addressInfoConfirmed"
              label="This information is correct"
            />
          )}
        </FormAreaWithAlert>

        <Grid item>
          <Typography variant="h5">Attorneys</Typography>
        </Grid>

        <Grid item>
          <ChipSelector
            name="attorneyInfos"
            options={virtualOffice.firmAttorneys.map(atty => atty.name)}
            label="Attorneys"
            textFieldStyle={classes.textField}
            disabled={virtualOfficeHasAddressFieldsUnderReview || !canUpdate}
            type="autocomplete"
            helperText={
              virtualOfficeHasAddressFieldsUnderReview
                ? `cannot edit attorneys while address changes are under review`
                : undefined
            }
          />
        </Grid>

        <Grid item>
          <Typography variant="h5">Office Staff Languages</Typography>
        </Grid>

        <Grid item>
          <ChipSelector
            name="languages"
            options={LANGUAGES}
            label="Office Staff Languages"
            textFieldStyle={classes.textField}
            type="autocomplete"
            disabled={!canUpdate}
          />
        </Grid>

        <Grid item>
          <FormAreaWithAlert active={Boolean(action === CONFIRM)}>
            <Typography variant="h5">Practice Areas</Typography>
            <Spacer v={16} />
            <AttorneyServicesSelector
              name="serviceTypes"
              disabled={!canPanelEditAttorneysAndServices || !canUpdate}
            />
            <Spacer v={10} />
            {action === CONFIRM && (
              <Checkbox
                type="checkbox"
                name="serviceInfoConfirmed"
                label="This information is correct"
              />
            )}
          </FormAreaWithAlert>
        </Grid>

        <ErrorMessage
          name="serviceTypes"
          render={msg => (
            <FormHelperText error style={{ fontSize: '16px', margin: '16px 16px 24px' }}>
              {msg}
            </FormHelperText>
          )}
        />

        <Grid item>
          <ChipSelector
            name="specialServices"
            options={firmOptions.specialServices}
            label="Special Services"
            textFieldStyle={classes.textField}
            disabled={!canPanelEditAttorneysAndServices || !canUpdate}
            type="autocomplete"
          />
        </Grid>

        {user?.isPanelRepUser && (
          <>
            <Grid item>
              <Typography variant="h5">Internal Use</Typography>
            </Grid>
            <Grid item>
              <SelectField
                name="status"
                label="Status"
                className={classes.textField}
                menuItems={[ACTIVE, ON_HOLD]}
                disabled={!canUpdate}
              />
            </Grid>
            <Grid item>
              <SelectField
                name="feeSchedule"
                label="Fee Schedule"
                menuItems={feeSchedules.map(feeSchedule => ({
                  value: feeSchedule.pk,
                  label: feeSchedule.description,
                }))}
                className={classes.textField}
                disabled={!canUpdate}
              />
            </Grid>
            <Grid item>
              <TextField
                name="engagementScore"
                label="Engagement Score"
                className={classes.textField}
                disabled={!user.groups.includes(CAN_EDIT_FEE_SCHEDULE) || !canUpdate}
              />
            </Grid>
          </>
        )}

        <Spacer v={24} />
        <OfficeFormButtons enabled={canUpdate} action={action} office={virtualOffice} />

        {(action === ADD || action === UPDATE) && (
          <>
            {updateVirtualOfficeMutation.isSuccess && (
              <Typography fontWeight="bold" color="primary">
                Office information successfully updated
              </Typography>
            )}
            <Typography fontWeight="bold" color="error" style={{ whiteSpace: 'pre-line' }}>
              {errorMessage}
            </Typography>
          </>
        )}
      </Grid>
      {showReactivateOfficeModal && (
        <ReactivateOfficeModal
          open={showReactivateOfficeModal}
          onClose={() => setShowReactivateOfficeModal(false)}
          addToCache={addToCache}
          afterSubmitAction={afterSubmitAction}
          firmAttorneys={virtualOffice.firmAttorneys}
        />
      )}
    </Form>
  );
};

export default observer(VirtualOfficeForm);
